/* One message's report — Logs and Analytics.
 *
 * WHAT EACH CHANNEL CAN HONESTLY CLAIM is the thing this screen exists to
 * get right. "Delivered" means three different things:
 *
 *   in the app   the bell entry was READ. We know this.
 *   push         FCM accepted the token. NOT the same as the phone showing
 *                it — the app may be uninstalled, the phone off for a week.
 *   SMS          a real carrier receipt, once DLT clears.
 *
 * So the server sends a `confirms` field per channel and the column is
 * labelled from it. A dashboard that reports 98% delivered while push
 * silently fails is worse than no dashboard.
 */
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Bell,
  Check,
  Loader2,
  MessageSquare,
  Smartphone,
  TriangleAlert,
} from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  useMe,
  useMessage,
  useMessageAnalytics,
  useMessageRecipients,
} from '@/api/queries'
import { useTabParam } from '@/lib/useTabParam'
import { cn, timeAgo } from '@/lib/utils'
import type { ChannelStats, MessageRecipient } from '@/types/api'

const CHANNEL_LABEL: Record<string, string> = {
  in_app: 'In the app',
  push: 'Push',
  sms: 'SMS',
}

const CHANNEL_ICON: Record<string, typeof Bell> = {
  in_app: Bell,
  push: Smartphone,
  sms: MessageSquare,
}

/* What the number under a channel MEANS. Read from the server's `confirms`
   so the wording cannot drift from what the backend actually knows. */
const CONFIRMS_LABEL: Record<string, string> = {
  read: 'opened it',
  accepted: 'accepted by Google',
  delivered: 'confirmed delivered',
}

export function MessageReportPage() {
  const { messageId = null } = useParams()
  const { data: me } = useMe()
  const churchId = me?.church_id ?? null

  const [tab, setTab] = useTabParam(['logs', 'analytics'] as const, 'logs')
  const message = useMessage(churchId, messageId)

  if (message.isLoading) {
    return (
      <Card className="p-s6 text-center text-ink-2">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Loading" />
      </Card>
    )
  }

  if (message.isError || !message.data) {
    return (
      <Card className="p-s6 text-center">
        <p className="font-semibold">This message could not be loaded.</p>
        <Link
          to="/messages"
          className="mt-s3 inline-block font-semibold text-brand hover:underline"
        >
          Back to messages
        </Link>
      </Card>
    )
  }

  const m = message.data

  return (
    <div className="space-y-s4">
      <Link
        to="/messages"
        className="inline-flex items-center gap-s2 text-base font-semibold text-ink-2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Messages
      </Link>

      <header>
        <h1 className="text-lg font-extrabold tracking-tight">{m.title}</h1>
        <p className="mt-1 text-base text-ink-2">{m.body}</p>
        <p className="mt-s2 text-sm text-ink-2">
          {m.audience_label} · {timeAgo(m.sent_at ?? m.created_at)}
          {m.author_name ? ` · ${m.author_name}` : ''}
        </p>
      </header>

      <div className="flex gap-s1 border-b border-line-2">
        <Tab active={tab === 'logs'} onClick={() => setTab('logs')}>
          Logs
        </Tab>
        <Tab active={tab === 'analytics'} onClick={() => setTab('analytics')}>
          Analytics
        </Tab>
      </div>

      {tab === 'logs' ? (
        <LogsTab churchId={churchId} messageId={messageId} />
      ) : (
        <AnalyticsTab churchId={churchId} messageId={messageId} />
      )}
    </div>
  )
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        '-mb-px border-b-2 px-s3 py-s2 text-md font-semibold transition-colors',
        active
          ? 'border-brand text-brand'
          : 'border-transparent text-ink-2 hover:text-ink',
      )}
    >
      {children}
    </button>
  )
}

/* ---------------------------------------------------------------- logs tab */

function LogsTab({
  churchId,
  messageId,
}: {
  churchId: string | null
  messageId: string | null
}) {
  const [channel, setChannel] = useState<string>('')
  const [status, setStatus] = useState<string>('')

  const rows = useMessageRecipients(churchId, messageId, {
    channel: channel || undefined,
    status: status || undefined,
  })

  const items = rows.data?.pages.flatMap((p) => p.items) ?? []

  return (
    <div className="space-y-s3">
      <div className="flex flex-wrap gap-s2">
        <Filter
          value={channel}
          onChange={setChannel}
          options={[
            { value: '', label: 'Every channel' },
            { value: 'in_app', label: 'In the app' },
            { value: 'push', label: 'Push' },
            { value: 'sms', label: 'SMS' },
          ]}
        />
        <Filter
          value={status}
          onChange={setStatus}
          options={[
            { value: '', label: 'Every outcome' },
            { value: 'sent', label: 'Sent' },
            { value: 'delivered', label: 'Delivered' },
            { value: 'failed', label: 'Failed' },
            { value: 'skipped', label: 'Not sent' },
            { value: 'pending', label: 'Waiting' },
          ]}
        />
      </div>

      {rows.isLoading ? (
        <Card className="p-s6 text-center text-ink-2">
          <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Loading" />
        </Card>
      ) : null}

      {!rows.isLoading && items.length === 0 ? (
        <Card className="p-s8 text-center text-base text-ink-2">
          Nothing matches those filters.
        </Card>
      ) : null}

      {items.length ? (
        <Card className="overflow-hidden p-0">
          <ul className="divide-y divide-line-2">
            {items.map((row) => (
              <LogRow key={row.id} row={row} />
            ))}
          </ul>
        </Card>
      ) : null}

      {rows.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            onClick={() => rows.fetchNextPage()}
            loading={rows.isFetchingNextPage}
          >
            Show more
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function LogRow({ row }: { row: MessageRecipient }) {
  const Icon = CHANNEL_ICON[row.channel] ?? Bell
  return (
    <li className="flex items-center gap-s3 px-s4 py-s3">
      <Avatar name={row.name} src={row.avatar_url} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold">{row.name}</div>
        <div className="flex flex-wrap items-center gap-x-s2 text-sm text-ink-2">
          <span className="inline-flex items-center gap-1">
            <Icon className="h-3 w-3" aria-hidden />
            {CHANNEL_LABEL[row.channel]}
          </span>
          {/* WHY, when there is a why. A status alone does not tell an admin
              whether to go and ask somebody to install the app. */}
          {row.detail ? (
            <>
              <span aria-hidden>·</span>
              <span>{row.detail}</span>
            </>
          ) : null}
          {row.read_at ? (
            <>
              <span aria-hidden>·</span>
              <span className="text-success">opened {timeAgo(row.read_at)}</span>
            </>
          ) : null}
        </div>
      </div>
      <Outcome status={row.status} />
    </li>
  )
}

function Outcome({ status }: { status: string }) {
  const tone =
    status === 'failed'
      ? 'bg-danger-soft text-danger-ink'
      : status === 'skipped'
        ? 'bg-surface-2 text-ink-2'
        : status === 'pending'
          ? 'bg-warning-soft text-warning-ink'
          : 'bg-success-soft text-success'
  const label =
    status === 'skipped'
      ? 'Not sent'
      : status === 'pending'
        ? 'Waiting'
        : status.charAt(0).toUpperCase() + status.slice(1)
  return (
    <span
      className={cn(
        'shrink-0 rounded-pill px-s3 py-0.5 text-xs font-bold',
        tone,
      )}
    >
      {label}
    </span>
  )
}

function Filter({
  value,
  onChange,
  options,
}: {
  value: string
  onChange: (next: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-line-2 bg-surface px-s3 py-s2 text-base font-semibold text-ink"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

/* ----------------------------------------------------------- analytics tab */

function AnalyticsTab({
  churchId,
  messageId,
}: {
  churchId: string | null
  messageId: string | null
}) {
  const report = useMessageAnalytics(churchId, messageId)

  if (report.isLoading) {
    return (
      <Card className="p-s6 text-center text-ink-2">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Loading" />
      </Card>
    )
  }

  if (!report.data) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        This report could not be loaded.
      </Card>
    )
  }

  const r = report.data

  return (
    <div className="space-y-s4">
      {/* REACH, in people. The headline number, because "how many of my
          congregation heard this?" is the question actually being asked. */}
      <Card className="p-s5 text-center">
        <div className="text-3xl font-extrabold tracking-tight">
          {r.reached}
          <span className="text-ink-3"> of {r.total_contacts}</span>
        </div>
        <p className="mt-1 text-base text-ink-2">
          people reached on at least one channel
        </p>
        {r.not_reached > 0 ? (
          <p className="mt-s2 inline-flex items-center gap-s2 rounded-pill bg-warning-soft px-s3 py-1 text-sm font-semibold text-warning-ink">
            <TriangleAlert className="h-3.5 w-3.5" aria-hidden />
            {r.not_reached} could not be reached at all
          </p>
        ) : null}
      </Card>

      <section className="space-y-s2">
        <h2 className="px-s1 text-md font-bold">By channel</h2>
        {r.channel_breakdown.map((c) => (
          <ChannelCard key={c.channel} stats={c} />
        ))}
      </section>

      {r.problems.length ? (
        <section className="space-y-s2">
          <h2 className="px-s1 text-md font-bold">Why people were missed</h2>
          <Card className="overflow-hidden p-0">
            <ul className="divide-y divide-line-2">
              {r.problems.map((p, i) => (
                <li
                  key={`${p.channel}-${p.status}-${i}`}
                  className="flex items-start gap-s3 px-s4 py-s3"
                >
                  <span className="shrink-0 rounded-pill bg-surface-2 px-s3 py-0.5 text-sm font-bold">
                    {p.count}
                  </span>
                  <div className="min-w-0">
                    <div className="text-base">{p.reason}</div>
                    <div className="text-sm text-ink-2">
                      {CHANNEL_LABEL[p.channel] ?? p.channel}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </section>
      ) : null}
    </div>
  )
}

function ChannelCard({ stats }: { stats: ChannelStats }) {
  const Icon = CHANNEL_ICON[stats.channel] ?? Bell
  /* The honest number for THIS channel. In-app can say how many opened it;
     push can only say Google took it. */
  const confirmed =
    stats.confirms === 'read'
      ? stats.read
      : stats.confirms === 'delivered'
        ? stats.delivered
        : stats.sent

  return (
    <Card className="p-s4">
      <div className="flex items-center gap-s3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-pill bg-surface-2 text-ink-2">
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold">{CHANNEL_LABEL[stats.channel]}</div>
          <div className="text-sm text-ink-2">
            {/* Labelled from what the server says this channel can prove. */}
            {confirmed ?? 0} of {stats.total} {CONFIRMS_LABEL[stats.confirms]}
          </div>
        </div>
        {stats.sent > 0 ? (
          <span className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-success">
            <Check className="h-3.5 w-3.5" aria-hidden />
            {stats.sent} sent
          </span>
        ) : null}
      </div>

      {stats.failed > 0 || stats.skipped > 0 || stats.pending > 0 ? (
        <div className="mt-s3 flex flex-wrap gap-x-s3 gap-y-1 border-t border-line-2 pt-s3 text-sm text-ink-2">
          {stats.failed > 0 ? (
            <span className="text-danger-ink">{stats.failed} failed</span>
          ) : null}
          {stats.skipped > 0 ? <span>{stats.skipped} not sent</span> : null}
          {stats.pending > 0 ? <span>{stats.pending} waiting</span> : null}
        </div>
      ) : null}
    </Card>
  )
}
